import { useState } from 'react';
import { ArrowInline } from "@/lib/Icon";
import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';

import {
  useCancelOrder,
  useClinicalState,
  useOrder,
  useOrders,
  useMySubscriptions,
} from '@/lib/api/hooks';
import { brand } from '@brand';
import { Button } from '@/design-system/primitives';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/app-shell';
import { supportChat } from '@/components/app-shell/support/supportChatUi';
import type { CancelOrderResponse, Order } from '@/types/api';
import { OrderProgress, OrderShipsAndPays, OrderWaitChip } from './OrderProgress';

export function cancellationSummary(result: CancelOrderResponse): string {
  if (result.cancellation_outcome === 'confirmed' && result.status === 'cancelled') {
    const subscription = result.subscription_cancelled
      ? ' Your subscription has been stopped.' : '';
    const followups = result.followups_pending
      ? ' Related account updates are still pending.' : '';
    return `Your order has been cancelled.${subscription}${followups}`;
  }
  if (result.cancellation_outcome === 'pending') {
    return 'Your cancellation request has been submitted. Pharmacy confirmation is pending.';
  }
  if (result.cancellation_outcome === 'refused') {
    return 'The pharmacy could not cancel this order. Please contact support for help.';
  }
  return 'Cancellation has not been confirmed. Please contact support for help.';
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** Always two decimals, so a payment breakdown's amounts line up. */
function cents2(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABEL: Record<string, string> = {
  awaiting_prescription: 'AWAITING RX',
  awaiting_pharmacy_routing: 'AWAITING ROUTING',
  pending_review: 'PENDING REVIEW',
  pending_approval: 'PENDING APPROVAL',
  processing: 'PROCESSING',
  shipped: 'SHIPPED',
  delivered: 'DELIVERED',
  cancelled: 'CANCELLED',
  failed: 'FAILED',
};

function statusLabel(status: string, awaitingConsult: boolean): string {
  if (status === 'awaiting_prescription' && awaitingConsult) {
    return 'AWAITING CONSULT';
  }
  return STATUS_LABEL[status] ?? status.replace(/_/g, ' ').toUpperCase();
}

/**
 * OrderDetail — mock-matched.
 *
 * Layout: order number title + ▶ subtitle + ← BACK TO ORDERS link.
 * ITEMS card lists each line with right-aligned subtotal, footer row
 * is TOTAL. SHIPPING card renders a vertical timeline with a sunken
 * left rail. Bottom: black Track Package CTA + sunken Request Refill
 * row. Cancel flow preserved (collapses into a tiny confirm).
 */
export function OrderDetail() {
  const { orderId } = useParams();
  const { data: order, isLoading, error } = useOrder(orderId);
  const { data: clinical } = useClinicalState();
  const needsSyncConsult = Boolean(clinical?.needs_sync_consult);
  const awaitingConsult = Boolean(clinical?.awaiting_consult);
  const cancel = useCancelOrder();
  // Cross-reference stripe_subscription_id → MeSubscription.plan_cycles
  // to surface a Monthly/Quarterly badge. /me/subscriptions is cached
  // app-wide so this is free after first paint.
  const { data: subsData, isLoading: subsLoading } = useMySubscriptions();
  const matchedSub = order?.stripe_subscription_id
    ? subsData?.subscriptions.find(
        (s) => s.stripe_subscription_id === order.stripe_subscription_id,
      )
    : null;
  // Storefront plans read like Account ("Every 3 months"), not "Quarterly".
  const cadenceLabel = matchedSub
    ? matchedSub.plan_cycles === 3 && brand.cycleDayPlanLabels
      ? 'QUARTERLY'
      : matchedSub.plan_cycles === 1
        ? 'MONTHLY'
        : `EVERY ${matchedSub.plan_cycles} MONTHS`
    : null;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellationResult, setCancellationResult] = useState<CancelOrderResponse | null>(null);
  // A later order refresh can settle the pharmacy request. It proves only
  // order cancellation, never subscription, refund, or other follow-up work.
  const pharmacyPendingSettled = order?.status === 'cancelled'
    && (cancellationResult?.cancellation_outcome === 'pending'
      || cancellationResult?.cancellation_outcome === 'uncertain'
      || cancellationResult?.cancellation_outcome === undefined);
  const currentCancellation = cancellationResult?.id === order?.id && !pharmacyPendingSettled
    ? cancellationResult : null;

  // Storefront: the plan price and discount rows come from the
  // subscription, so the page waits for it instead of growing later.
  if (isLoading || (brand.planOrderPayments && subsLoading && !subsData)) {
    if (brand.stableSkeletons) {
      return (
        <div className="flex flex-col" aria-busy="true" aria-label="Loading">
          <div className="bg-[var(--surface-sunken)] px-15 md:px-40 pt-25 pb-15">
            <div className="max-w-[58rem] mx-auto">
              <div className="h-[3.6rem] w-[22rem] max-w-full bg-[#E8E8E8] animate-pulse" />
              <div className="h-[1.8rem] w-[26rem] max-w-full mt-10 bg-[#E8E8E8] animate-pulse" />
            </div>
          </div>
          <div className="flex flex-col px-15 md:px-40 py-25 gap-25 max-w-[65rem] mx-auto w-full">
            <div className="h-[2.4rem]" />
            <div className="h-[24rem] border border-line-subtle bg-[var(--surface-raised)] animate-pulse" />
            <div className="h-[24rem] border border-line-subtle bg-[var(--surface-raised)] animate-pulse" />
          </div>
        </div>
      );
    }
    return <div className="h-[40rem] bg-[var(--surface-sunken)] animate-pulse" />;
  }
  if (error || !order) {
    return (
      <div className="max-w-form">
        <Link
          to="/app/orders"
          className="inline-flex items-center gap-5 type-label-sm text-ink hover:text-ink-secondary"
        >
          <span aria-hidden>←</span> BACK TO ORDERS
        </Link>
        <div className="mt-20 type-body-md text-ink-secondary">
          Couldn&apos;t load this order.
        </div>
      </div>
    );
  }

  const created = parseISO(order.created_at);
  const carrier = order.shipment?.carrier?.toUpperCase();
  const shippedAt = order.shipment?.shipped_at
    ? parseISO(order.shipment.shipped_at)
    : null;
  const delivered = order.shipment?.delivered_at
    ? parseISO(order.shipment.delivered_at)
    : null;

  return (
    <div className="flex flex-col">
      <PageHeader
        title={order.order_number}
        description={`This order was placed on ${format(created, 'MMMM d, yyyy')}.`}
      />
      <div className="flex flex-col px-15 md:px-40 py-25 gap-25 max-w-[65rem] mx-auto w-full">

      <div className="flex items-center justify-between gap-10">
        <Link
          to="/app/orders"
          className={cn(
            'inline-flex items-center underline gap-8 type-label-sm text-ink-muted hover:text-ink self-start',
            // Storefront: one line beside the status chips.
            brand.orderReviewSteps && 'whitespace-nowrap shrink-0 min-h-44',
          )}
        >
          {/* <span aria-hidden className="inline-flex items-center justify-center w-10 h-12 rotate-180">
            <Icon name="Arrow" viewBox="0 0 10 12" />
          </span> */}
          Back to orders
        </Link>
  
        {/* Status pill row (only when not the basic 'placed' state — the
            header already says when the order was placed). */}
        {(order.status !== 'pending_review' || cadenceLabel) && (
          <div className="flex items-center gap-8 flex-wrap">
            {order.status !== 'pending_review' &&
              (brand.orderReviewSteps ? (
                <OrderWaitChip order={order} />
              ) : (
                <span className="type-label-xs text-ink-secondary bg-[var(--surface-sunken)] px-10 py-5">
                  {statusLabel(order.status, awaitingConsult)}
                </span>
              ))}
            {cadenceLabel && (
              <span className="type-label-xs bg-ink text-white px-10 py-5">
                {cadenceLabel}
              </span>
            )}

          </div>
        )}
        {order.issue && (
          <div className="type-body-sm text-warning mt-10">
            {order.issue}{' '}
            {!order.fulfillment_conflict && /address/i.test(order.issue) ? (
              <Link to="/app/account" className="underline underline-offset-4 font-medium">
                Update address <ArrowInline />
              </Link>
            ) : (
              <button type="button" onClick={() => supportChat.open()}
                className="underline underline-offset-4 font-medium">
                Contact support <ArrowInline />
              </button>
            )}
          </div>
        )}
      </div>

      {order.status === 'awaiting_prescription' && awaitingConsult && !brand.orderReviewSteps && (
        <section className="border border-line-subtle bg-[var(--surface-sunken)] px-20 py-15">
          <p className="type-label-sm text-ink-secondary">
            AWAITING CONSULT
          </p>
          <p className="type-body-md text-ink mt-8">
            {needsSyncConsult ? (
              <>
                Your state requires a live visit with a clinician before
                your prescription can be written. Book a time on the{' '}
                <Link
                  to="/app/clinic?tab=appointments"
                  className="underline underline-offset-4"
                >
                  Clinic
                </Link>{' '}
                tab — your script is written after the visit. Cancelling
                or rescheduling resets the wait.
              </>
            ) : (
              <>
                Your visit is on the calendar. Your clinician will write
                your prescription after the appointment — manage it from
                the{' '}
                <Link
                  to="/app/clinic?tab=appointments"
                  className="underline underline-offset-4"
                >
                  Clinic
                </Link>{' '}
                tab. Cancelling resets the wait.
              </>
            )}
          </p>
        </section>
      )}

      {/* ITEMS */}
      <section className="border border-line-subtle bg-[var(--surface-raised)]">
        <div className="px-15 py-12 bg-[var(--surface-raised)] border-b border-line-subtle">
          <span className="type-label-sm text-ink-secondary">ITEMS</span>
        </div>
        <ul className="flex flex-col">
          {order.lines.map((l, i) => (
            <li
              key={i}
              className="flex items-baseline justify-between gap-20 px-15 py-15 border-b border-line-subtle last:border-b-0"
            >
              <span className="type-label-md truncate">{l.name}</span>
              <span className="type-data-md shrink-0">
                {dollars(l.unit_price_cents * l.quantity)}
              </span>
            </li>
          ))}
        </ul>
        {/* Storefront: the PAID box below states the total once. */}
        {!(brand.planOrderPayments && order.amount_paid_cents != null) && (
          <div className="flex items-baseline justify-between px-15 py-15 border-t border-line-subtle">
            <span className="type-label-sm text-ink-secondary">ORDER TOTAL</span>
            <span className="type-label-md">{dollars(order.total_cents)}</span>
          </div>
        )}
        {order.amount_paid_cents != null && brand.planOrderPayments && (
          // Storefront: the order's own charge. A cart's orders each report
          // their share of the one card charge, so no "may cover multiple
          // items" note; a first-charge discount shows against the plan
          // price (the subscription's renewal price).
          <div className="px-15 py-15 border-t border-line-subtle bg-[var(--surface-sunken)] flex flex-col gap-8">
            {matchedSub?.unit_amount_cents != null &&
              matchedSub.unit_amount_cents > order.amount_paid_cents && (
                <>
                  <div className="flex items-baseline justify-between gap-15 type-body-sm text-ink-secondary">
                    <span>Plan price</span>
                    <span className="tabular-nums">{cents2(matchedSub.unit_amount_cents)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-15 type-body-sm text-ink-secondary">
                    <span>First-charge discount</span>
                    <span className="tabular-nums">
                      −{cents2(matchedSub.unit_amount_cents - order.amount_paid_cents)}
                    </span>
                  </div>
                </>
              )}
            <div className="flex items-baseline justify-between gap-15">
              <span className="type-label-sm text-ink-secondary">PAID</span>
              <span className="type-label-md">{cents2(order.amount_paid_cents)}</span>
            </div>
            <CartChargeNote order={order} />
          </div>
        )}
        {order.amount_paid_cents != null && !brand.planOrderPayments && (
          <div className="px-15 py-15 border-t border-line-subtle bg-[var(--surface-sunken)]">
            <div className="flex items-baseline justify-between gap-15">
              <span className="type-label-sm text-ink-secondary">INVOICE / CHARGE PAID</span>
              <span className="type-label-md">{dollars(order.amount_paid_cents)}</span>
            </div>
            <p className="type-body-sm text-ink-muted mt-8">
              This payment may cover multiple items or scheduled shipments.
            </p>
          </div>
        )}
      </section>

      {/* SHIPPING timeline (storefront: the whole order's progress) */}
      <section className="border border-line-subtle bg-[var(--surface-raised)]">
        <div className="px-15 py-12 bg-[var(--surface-raised)] border-b border-line-subtle">
          <span className="type-label-sm text-ink-secondary">
            {brand.orderReviewSteps ? 'PROGRESS' : 'SHIPPING'}
          </span>
        </div>
        {brand.orderReviewSteps ? (
          <OrderProgress order={order} />
        ) : (
          <ShippingTimeline
            orderCreatedAt={created}
            shippedAt={shippedAt}
            deliveredAt={delivered}
            carrier={carrier}
            status={order.status}
          />
        )}
      </section>

      {brand.orderReviewSteps && (
        <section className="border border-line-subtle bg-[var(--surface-raised)]">
          <div className="px-15 py-12 bg-[var(--surface-raised)] border-b border-line-subtle">
            <span className="type-label-sm text-ink-secondary">DELIVERY AND PAYMENT</span>
          </div>
          <OrderShipsAndPays />
        </section>
      )}

      {/* CTAs */}
      <div className="flex flex-col gap-10">
        {brand.orderReviewSteps && !order.shipment?.tracking_url ? null : order.shipment?.tracking_url ? (
          <Button asChild className="w-full justify-between h-50 px-15">
            <a
              href={order.shipment.tracking_url}
              target="_blank"
              rel="noreferrer"
            >
              Track Package
              <ExternalLink size={14} strokeWidth={1.5} />
            </a>
          </Button>
        ) : (
          <Button disabled className="w-full justify-between h-50 px-15">
            Track Package
            <ExternalLink size={14} strokeWidth={1.5} />
          </Button>
        )}
        {/* Request Refill: intentionally hidden — refill flow is automatic
            (subscription cycles + invoice.paid webhook re-fires fulfillment).
            See task #199 / Orders comment policy. */}
        {order.cancellable && !showCancelConfirm && (
          <button
            type="button"
            onClick={() => setShowCancelConfirm(true)}
            className="inline-flex items-center gap-8 self-start type-label-sm text-ink-muted hover:text-ink mt-5"
          >
            <X size={12} strokeWidth={1.5} /> Cancel order
          </button>
        )}
        {showCancelConfirm && (
          <div className="border border-line-subtle px-15 py-15">
            <p className="type-body-sm">
              {order.is_subscription
                ? 'Cancel this fill AND stop the subscription?'
                : 'Cancel this order?'}
            </p>
            <div className="flex items-center gap-10 mt-15">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancel.isPending}
              >
                Keep
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={cancel.isPending}
                onClick={() =>
                  cancel
                    .mutateAsync(order.id)
                    .then((result) => {
                      setCancellationResult(result);
                      if (result.id === order.id && result.cancellation_outcome === 'confirmed'
                          && result.status === 'cancelled') {
                        track('order_cancelled', { order_id: order.id });
                      }
                      setShowCancelConfirm(false);
                    })
                    .catch(() => {})
                }
              >
                Yes, cancel
              </Button>
            </div>
            {cancel.error && (
              <div className="type-body-sm text-danger mt-10">
                {(cancel.error as { message?: string }).message ??
                  'Cancel failed'}
              </div>
            )}
          </div>
        )}
        {currentCancellation && (
          <div role="status" className="type-body-sm text-ink-muted">
            {cancellationSummary(currentCancellation)}
          </div>
        )}
        {!currentCancellation && order.status === 'cancelled' && (
          <div className="type-body-sm text-danger">
            This order has been cancelled.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

/** A cart's orders are created a few seconds apart by the one payment's
 *  webhook; separate checkouts are minutes apart at the least. */
const CART_ORDER_GAP_MS = 60_000;

/**
 * The orders paid by the same card charge as `order`: it and the orders
 * created within CART_ORDER_GAP_MS of each other around it. `/me/orders`
 * does not tie a cart's orders together, so this reads their creation
 * times.
 */
function cartOrders(order: Order, all: Order[]): Order[] {
  const sorted = [...all].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const at = sorted.findIndex((o) => o.id === order.id);
  if (at < 0) return [order];
  const gap = (a: Order, b: Order) => Math.abs(Date.parse(b.created_at) - Date.parse(a.created_at));
  let start = at;
  while (start > 0 && gap(sorted[start - 1], sorted[start]) <= CART_ORDER_GAP_MS) start -= 1;
  let end = at;
  while (end < sorted.length - 1 && gap(sorted[end], sorted[end + 1]) <= CART_ORDER_GAP_MS) end += 1;
  return sorted.slice(start, end + 1);
}

/**
 * Storefront: when the card was charged, and for a cart (one card charge,
 * one order per item) the whole charge, so the amount on the patient's
 * statement appears here ("Your share of the $988.20 card charge on
 * October 23, 2026 (2 treatments)").
 */
function CartChargeNote({ order }: { order: Order }) {
  const { data: all } = useOrders();
  const date = format(parseISO(order.created_at), 'MMMM d, yyyy');
  const cart = all ? cartOrders(order, all) : [order];
  const shares = cart.map((o) => o.amount_paid_cents);
  if (cart.length > 1 && shares.every((c): c is number => c != null)) {
    const total = shares.reduce((sum, c) => sum + c, 0);
    return (
      <p className="type-body-sm text-ink-muted">
        Your share of the {cents2(total)} card charge on {date} ({cart.length} treatments).
      </p>
    );
  }
  return <p className="type-body-sm text-ink-muted">Charged to your card on {date}.</p>;
}

function ShippingTimeline({
  orderCreatedAt,
  shippedAt,
  deliveredAt,
  carrier,
  status,
}: {
  orderCreatedAt: Date;
  shippedAt: Date | null;
  deliveredAt: Date | null;
  carrier?: string;
  status: string;
}) {
  type Step = { label: string; date: string | null; done: boolean };
  const isCancelled = status === 'cancelled' || status === 'failed';
  // The backend projects shipment evidence into status for every surface.
  // Timestamps and tracking labels must not independently advance this timeline.
  const isDelivered = status === 'delivered';
  const isShipped = isDelivered || status === 'shipped';
  const isProcessing =
    isShipped || status === 'processing' || status === 'pending_review';

  const steps: Step[] = [
    {
      label: 'Order placed',
      date: format(orderCreatedAt, 'MMM d, yyyy'),
      done: true,
    },
    {
      label: carrier ? `Shipped via ${carrier}` : 'Shipped',
      date: isShipped && shippedAt ? format(shippedAt, 'MMM d, yyyy') : isProcessing && !isShipped ? 'Awaiting carrier handoff' : null,
      done: isShipped,
    },
    {
      label: 'Delivered',
      date: isDelivered && deliveredAt ? format(deliveredAt, 'MMM d, yyyy') : null,
      done: isDelivered,
    },
  ];
  if (isCancelled) {
    steps.splice(1, 2, { label: status === 'failed' ? 'Failed' : 'Cancelled', date: null, done: true });
  }

  return (
    <ol className="flex flex-col">
      {steps.map((s, i) => (
        <li
          key={i}
          className={cn(
            'flex items-stretch',
            i < steps.length - 1 && 'border-b border-line-subtle',
          )}
        >
          {/* Icon cell — flex item with its own background so we can
              color it per-row (done vs pending). */}
          <div
            aria-hidden
            className={cn(
              'w-60 min-h-60 shrink-0 flex items-center justify-center border-r border-line-subtle',
              s.done
                ? 'bg-accent text-white'
                : 'bg-[var(--surface-sunken)] text-ink-muted',
            )}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
              <path d="M3 8L12 3l9 5v8l-9 5-9-5V8z" />
              <path d="M3 8l9 5 9-5" />
              <path d="M12 13v9" />
            </svg>
          </div>
          <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5 justify-center">
            <div className="type-label-sm text-ink leading-tight">
              {s.label.toUpperCase()}
            </div>
            {s.date && (
              <div className="type-data-sm text-ink-muted">{s.date}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
